import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Card } from 'primereact/card';
import { useLocation, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import { getAlertSuppressionReasonsInit, getAlertSuppressionScopeInit, postAlertSuppressionInformationInit } from '../redux/actions';
import { addDateTime } from '../../../../common/date-time';
import "./ReseLeakAlertSuppressionRequest.scss";

const ReseLeakAlertSuppressionRequest = () => {

    const [alertInfo, setAlertInfo] = useState({
        storeId: '',
        assetName: '',
        alertName: '',
        pointId: '',
        division: ''
    });
    const [alertInfoError, setAlertInfoError] = useState(false);
    const [suppressionConfirmDialog, setSuppressionConfirmDialog] = useState(false);
    const initialSuppressionFrom = useMemo(() => addDateTime("minutes", 15, new Date()), []);
    const initialSuppressionTill = useMemo(() => addDateTime("day", 1, initialSuppressionFrom), []);

    const alertSuppressionReasons = useSelector(state => state.reseAlertSuppression.alertSuppressionReasons);
    const alertSuppressionScope = useSelector(state => state.reseAlertSuppression.alertSuppressionScope);
    const dispatch = useDispatch();
    const history = useHistory();

    const defaultState = {
        storeId: alertInfo.storeId,
        assetName: alertInfo.assetName,
        alertName: alertInfo.alertName,
        division: alertInfo.division,
        reason: '',
        scope: '',
        notes: '',
        suppressionFrom: initialSuppressionFrom,
        suppressionTill: initialSuppressionTill
    };

    const {
        values,
        touched,
        handleChange,
        setFieldValue,
        handleSubmit,
        errors,
        resetForm
    } = useFormik({
        enableReinitialize: true,
        initialValues: defaultState,
        validate: (values) => {
            let errors = {};
            validateReason(values, errors);
            validateNotes(values, errors);
            validateSuppressionFrom(values, errors);
            validateSuppressionTill(values, errors);
            return errors;
        },
        onSubmit: (values) => {
            setSuppressionConfirmDialog(true);
        }
    });

    const submitHandler = () => {
        if (!alertInfo.storeId || alertInfo.storeId === "No data" || !alertInfo.assetName || alertInfo.assetName === "No data" ||
            !alertInfo.alertName || alertInfo.alertName === "No data") {
            setAlertInfoError(true);
            return;
        }
        const payload = {
            store_id: values.storeId,
            asset_name: values.assetName,
            alert_name: values.alertName,
            division: alertInfo.division,
            work_order: values.workOrder,
            suppression_till: values.suppressionTill,
            suppression_from: values.suppressionFrom,
            reason: values.reason,
            notes: values.notes,
            work_status: null
        }
        dispatch(postAlertSuppressionInformationInit(payload));
        clearForm();
        setTimeout(() => {
            history.push({
                pathname: '/admin/rese/alert-suppression-logs',
                state: { activeTab: 1 }
            });
        }, 3000);
    }

    const rejectHandler = () => {
        setSuppressionConfirmDialog(false);
    }

    const useQuery = () => {
        return new URLSearchParams(useLocation().search);
    };

    const query = useQuery();

    useEffect(() => {
        const params = {
            storeId: query.get('storeId') || "No data",
            assetName: query.get('assetName') || "No data",
            alertName: query.get('alertName') || "No data",
            division: query.get('division') || "No data",
        };
        setAlertInfo(params);
    }, []);

    useEffect(() => {
        dispatch(getAlertSuppressionReasonsInit());
        dispatch(getAlertSuppressionScopeInit());
    }, [])

    const clearForm = () => {
        resetForm();
    }

    const validateReason = (values, errors) => {
        if (!values.reason) {
            errors.reason = "Please enter the reason";
        }
    }

    const validateNotes = (values, errors) => {
        if (!values.notes) {
            errors.notes = "Please enter the notes";
        }
        else if (values.notes.length > 150) {
            errors.notes = "notes should not be more than 150 characters";
        }
    }

    const validateSuppressionFrom = (values, errors) => {
        if (values.suppressionFrom <= addDateTime("minutes", 14, new Date())) {
            errors.suppressionFrom = "Suppression start datetime should be atleast 15 mins from current datetime";
        }
    }

    const validateSuppressionTill = (values, errors) => {
        if (values.suppressionTill < addDateTime("minutes", 15, values.suppressionFrom)) {
            errors.suppressionTill = "Suppression end datetime should be atleast 15 mins from suppression start datetime";
        }
    }

    const computeDuration = () => {
        if (!values.suppressionFrom || !values.suppressionTill) return null;
        
        const from = new Date(values.suppressionFrom);
        const till = new Date(values.suppressionTill);
        const diffMs = till - from;
        
        if (diffMs <= 0) return null;
        
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        let durationText = 'Duration: ';
        if (diffDays > 0) durationText += `${diffDays} day${diffDays > 1 ? 's' : ''} `;
        if (diffHours > 0) durationText += `${diffHours} hour${diffHours > 1 ? 's' : ''} `;
        if (diffMinutes > 0) durationText += `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
        
        return durationText.trim();
    };

    const messageTemplate = () => {
        return (
            <div className="confirmDialog-content">
                <div>
                    <p>Suppress the Alert named <strong>{values.alertName} </strong> For : </p>
                </div>
                <div className='row input-field'>
                    <div className='col-12 col-lg-5'>
                        <label>Store ID</label>
                    </div>
                    <div className='col-12 col-lg-7'>
                        <strong>{values.storeId}</strong>
                    </div>
                </div>
                <div className='row input-field'>
                    <div className='col-12 col-lg-5'>
                        <label>Asset Name</label>
                    </div>
                    <div className='col-12 col-lg-7'>
                        <strong>{values.assetName}</strong>
                    </div>
                </div>
                <div className='row input-field'>
                    <div className='col-12 col-lg-5'>
                        <label>Alert Name</label>
                    </div>
                    <div className='col-12 col-lg-7'>
                        <strong>{values.alertName}</strong>
                    </div>
                </div>
            </div>
        )
    }

    const alertInfoHeader = (
        <div className="card-header-custom">
            <i className="pi pi-info-circle"></i>
            <span>Alert Information</span>
        </div>
    );

    const alertConfigHeader = (
        <div className="card-header-custom">
            <i className="pi pi-cog"></i>
            <span>Alert Configuration</span>
        </div>
    );

    return (
        <div className="rese-alert-suppression-request-container container-fluid">
            <p className="page-label">
                RESE
                <i className="pi pi-angle-right" />
                Leak Detection Alert Suppression Request
            </p>

            <div className="cards-container">
                {/* Alert Information Card */}
                <Card header={alertInfoHeader} className="info-card">
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Division</span>
                            <span className="info-value">{alertInfo.division}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Store ID</span>
                            <span className="info-value">{alertInfo.storeId}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Asset Name</span>
                            <span className="info-value">{alertInfo.assetName}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Alert Name</span>
                            <span className="info-value">{alertInfo.alertName}</span>
                        </div>
                        
                    </div>
                    {!!alertInfoError && <p className="p-error alert-info-error-text">All fields in the Alert information is required to submit the suppression request</p>}
                </Card>

                {/* Alert Configuration Card */}
                <Card header={alertConfigHeader} className="config-card">
                    <form onSubmit={handleSubmit}>
                        <ConfirmDialog group="declarative" style={{ width: "40%" }} visible={suppressionConfirmDialog} onHide={() => setSuppressionConfirmDialog(false)}
                            message={messageTemplate}
                            header="Suppression Rule Confirmation" accept={submitHandler} reject={rejectHandler} />

                        <div className="config-grid">
                            {/* First Row - 3 columns */}
                            <div className="config-row-2">
                                <div className="form-field">
                                    <label htmlFor="workOrder">Work Order</label>
                                    <InputText
                                        id="workOrder"
                                        name="workOrder"
                                        value={values.workOrder}
                                        onChange={handleChange}
                                        className="w-full"
                                    />
                                </div>

                                <div className="form-field">
                                    <label htmlFor="reason">Reason</label>
                                    <Dropdown
                                        id="reason"
                                        name="reason"
                                        options={alertSuppressionReasons}
                                        optionLabel="alert_suppression_reason"
                                        optionValue="alert_suppression_reason"
                                        value={values.reason}
                                        onChange={handleChange}
                                        placeholder="Select Reason"
                                        className="w-full"
                                    />
                                    {!!touched.reason && errors.reason && <span className="p-error">{errors.reason}</span>}
                                </div>
                            </div>

                            {/* Second Row - Notes full width */}
                            <div className="config-row-full">
                                <div className="form-field">
                                    <label htmlFor="notes">Notes</label>
                                    <InputText
                                        id="notes"
                                        name="notes"
                                        value={values.notes}
                                        onChange={handleChange}
                                        className="w-full"
                                    />
                                    {!!touched.notes && errors.notes && <span className="p-error">{errors.notes}</span>}
                                </div>
                            </div>

                            {/* Third Row - Date pickers */}
                            <div className="config-row-2">
                                <div className="form-field">
                                    <label htmlFor="suppressionFrom">Suppression From</label>
                                    <Calendar
                                        id="suppressionFrom"
                                        name="suppressionFrom"
                                        value={values.suppressionFrom}
                                        onChange={(e) => setFieldValue("suppressionFrom", e.value)}
                                        showTime
                                        showIcon
                                        className="w-full"
                                    />
                                    {!!touched.suppressionFrom && errors.suppressionFrom && <span className="p-error">{errors.suppressionFrom}</span>}
                                </div>

                                <div className="form-field">
                                    <label htmlFor="suppressionTill">Suppression Till</label>
                                    <Calendar
                                        id="suppressionTill"
                                        name="suppressionTill"
                                        value={values.suppressionTill}
                                        onChange={(e) => setFieldValue("suppressionTill", e.value)}
                                        showTime
                                        showIcon
                                        className="w-full"
                                    />
                                    {!!touched.suppressionTill && errors.suppressionTill && <span className="p-error">{errors.suppressionTill}</span>}
                                </div>
                            </div>
                        </div>

                        <div className="button-group">
                            <Button label="Submit" type="submit" icon="pi pi-check" />
                            <Button label="Clear" type="reset" onClick={clearForm} className="p-button-secondary" icon="pi pi-times" />
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
}
export default ReseLeakAlertSuppressionRequest;